"use client";

/**
 * One call recording at a time, app-wide.
 *
 * The <audio> element lives here, in the layout, so a recording keeps playing
 * while the agent moves between tabs, pages and leads. Every player on screen
 * (lead History, the lead's call table, Communication, Agent Details) is a view
 * of this one element; CallRecordingBar controls it from anywhere.
 *
 * Two contexts on purpose: controls change on play/pause/load, the clock ticks
 * ~4x a second. Lists that only need play() never re-render on the tick.
 *
 * A live call takes over the headset: an incoming ring or a connected call
 * pauses the recording (it doesn't resume on its own).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { getProxyRecordingUrl, loadRecordingAsObjectUrl } from "@/app/_module/lib/utils";
import { useIncomingCall } from "@/app/_module/contexts/IncomingCallContext";
import { useVoiceDeviceContext } from "@/app/_module/contexts/VoiceDeviceProvider";

export interface CallRecording {
  /** call_histories.id; also keys the transcript */
  callId: string;
  recordingUrl: string;
  /** Lead or contact name, else the phone number */
  title: string;
  direction?: "inbound" | "outbound";
  callDate?: string | Date | null;
  /** Recording length from the call row; the audio's own length wins once loaded */
  durationSecs?: number | null;
  leadId?: string | null;
}

export type RecordingStatus = "loading" | "playing" | "paused" | "error";

interface Controls {
  current: CallRecording | null;
  status: RecordingStatus;
  rate: number;
  /** Load (if needed) and play `rec`, optionally from `at` seconds. */
  play: (rec: CallRecording, at?: number) => Promise<void>;
  toggle: () => void;
  seek: (secs: number) => void;
  skip: (deltaSecs: number) => void;
  setRate: (rate: number) => void;
  close: () => void;
}

interface Clock {
  time: number;
  duration: number;
}

export const PLAYBACK_RATES = [1, 1.25, 1.5, 2] as const;
// Same key the old per-row players saved the agent's speed under.
const RATE_KEY = "audioPlaybackRate";

const ControlsContext = createContext<Controls | null>(null);
const ClockContext = createContext<Clock>({ time: 0, duration: 0 });

/** null outside the provider; callers render a disabled player. */
export const useCallRecording = () => useContext(ControlsContext);
export const useCallRecordingClock = () => useContext(ClockContext);

const finite = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);

export function CallRecordingProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const objectUrl = useRef<string | null>(null);
  const loadedFor = useRef<string | null>(null); // callId whose bytes are in the element
  const loadSeq = useRef(0); // a newer play() wins over a slow load still in flight
  const loading = useRef(false);
  const rateRef = useRef(1);
  const currentRef = useRef<CallRecording | null>(null);

  const [current, setCurrent] = useState<CallRecording | null>(null);
  const [status, setStatus] = useState<RecordingStatus>("paused");
  const [rate, setRateState] = useState(1);
  const [clock, setClock] = useState<Clock>({ time: 0, duration: 0 });

  useEffect(() => {
    try {
      const saved = Number(localStorage.getItem(RATE_KEY));
      if ((PLAYBACK_RATES as readonly number[]).includes(saved)) {
        rateRef.current = saved;
        setRateState(saved);
      }
    } catch {
      /* private mode: stay at 1x */
    }
  }, []);

  const unload = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    objectUrl.current = null;
    loadedFor.current = null;
  }, []);

  useEffect(() => unload, [unload]);

  const play = useCallback(
    async (rec: CallRecording, at?: number) => {
      const audio = audioRef.current;
      if (!audio) return;

      if (loadedFor.current !== rec.callId) {
        const seq = ++loadSeq.current;
        loading.current = true;
        unload();
        currentRef.current = rec;
        setCurrent(rec);
        setStatus("loading");
        setClock({ time: at ?? 0, duration: Number(rec.durationSecs) || 0 });
        try {
          // <audio src> can't send the JWT, so the bytes come through the proxy as a blob.
          const url = await loadRecordingAsObjectUrl(getProxyRecordingUrl(rec.recordingUrl) || rec.recordingUrl);
          if (seq !== loadSeq.current) {
            URL.revokeObjectURL(url);
            return;
          }
          objectUrl.current = url;
          loadedFor.current = rec.callId;
          audio.src = url;
        } catch {
          if (seq === loadSeq.current) setStatus("error");
          return;
        } finally {
          if (seq === loadSeq.current) loading.current = false;
        }
      }

      // A new source resets playbackRate to defaultPlaybackRate.
      audio.defaultPlaybackRate = rateRef.current;
      audio.playbackRate = rateRef.current;
      if (at != null) {
        // WebKit ignores currentTime before metadata; wait for it.
        if (audio.readyState >= 1) audio.currentTime = at;
        else audio.addEventListener("loadedmetadata", () => { audio.currentTime = at; }, { once: true });
      }
      try {
        await audio.play();
      } catch {
        setStatus("paused"); // autoplay rules after a slow load: one more tap plays it
      }
    },
    [unload],
  );

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    const rec = currentRef.current;
    if (!audio || !rec) return;
    if (audio.paused || loadedFor.current !== rec.callId) void play(rec);
    else audio.pause();
  }, [play]);

  const seek = useCallback((secs: number) => {
    const audio = audioRef.current;
    if (!audio || !loadedFor.current) return;
    const end = finite(audio.duration) || Number(currentRef.current?.durationSecs) || secs;
    const t = Math.min(Math.max(0, secs), end);
    audio.currentTime = t;
    setClock((c) => ({ ...c, time: t }));
  }, []);

  const skip = useCallback((delta: number) => {
    const audio = audioRef.current;
    if (audio) seek(audio.currentTime + delta);
  }, [seek]);

  const setRate = useCallback((r: number) => {
    rateRef.current = r;
    setRateState(r);
    const audio = audioRef.current;
    if (audio) {
      audio.defaultPlaybackRate = r;
      audio.playbackRate = r;
    }
    try {
      localStorage.setItem(RATE_KEY, String(r));
    } catch {
      /* not persisted; fine */
    }
  }, []);

  const close = useCallback(() => {
    loadSeq.current++;
    loading.current = false;
    unload();
    currentRef.current = null;
    setCurrent(null);
    setStatus("paused");
    setClock({ time: 0, duration: 0 });
  }, [unload]);

  // A ringing or connected call owns the headset.
  const { activeCallSid } = useVoiceDeviceContext();
  const { isCallVisible } = useIncomingCall();
  useEffect(() => {
    if (activeCallSid || isCallVisible) audioRef.current?.pause();
  }, [activeCallSid, isCallVisible]);

  const controls = useMemo<Controls>(
    () => ({ current, status, rate, play, toggle, seek, skip, setRate, close }),
    [current, status, rate, play, toggle, seek, skip, setRate, close],
  );

  return (
    <ControlsContext.Provider value={controls}>
      <ClockContext.Provider value={clock}>
        {children}
        <audio
          ref={audioRef}
          preload="auto"
          hidden
          onPlay={() => setStatus("playing")}
          onPause={() => { if (!loading.current) setStatus("paused"); }}
          onEnded={(e) => {
            e.currentTarget.currentTime = 0;
            setStatus("paused");
          }}
          onTimeUpdate={(e) => {
            const a = e.currentTarget;
            setClock({ time: a.currentTime, duration: finite(a.duration) || Number(currentRef.current?.durationSecs) || 0 });
          }}
          onDurationChange={(e) => {
            const d = finite(e.currentTarget.duration);
            if (d) setClock((c) => ({ ...c, duration: d }));
          }}
          onError={() => { if (loadedFor.current) setStatus("error"); }}
        />
      </ClockContext.Provider>
    </ControlsContext.Provider>
  );
}
